import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ShoppingCartIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const RecentlyViewed = ({ maxItems = 8, showTitle = true, className = '' }) => {
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [wishlist, setWishlist] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    loadRecentlyViewed();
    if (user) {
      loadUserWishlist();
    }
  }, [user]);

  const loadRecentlyViewed = () => {
    try {
      setLoading(true);
      const stored = localStorage.getItem('recentlyViewed');
      if (stored) {
        const products = JSON.parse(stored);
        setRecentlyViewed(products.slice(0, maxItems));
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserWishlist = async () => {
    try {
      const response = await axios.get('/api/wishlist');
      const wishlistProductIds = response.data.wishlist.map(item => item.product.id);
      setWishlist(new Set(wishlistProductIds));
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const removeFromRecentlyViewed = (productId) => {
    try {
      const stored = localStorage.getItem('recentlyViewed');
      if (stored) {
        const products = JSON.parse(stored);
        const filtered = products.filter(p => p.id !== productId);
        localStorage.setItem('recentlyViewed', JSON.stringify(filtered));
        setRecentlyViewed(filtered.slice(0, maxItems));
      }
    } catch (error) {
      console.error('Error removing from recently viewed:', error);
    }
  };

  const clearRecentlyViewed = () => {
    localStorage.removeItem('recentlyViewed');
    setRecentlyViewed([]);
  };

  const handleAddToCart = async (product, e) => {
    if (e) e.stopPropagation();
    try {
      await addToCart(product, 1);
      toast.success('המוצר נוסף לעגלה');
    } catch (error) {
      toast.error('שגיאה בהוספה לעגלה');
    }
  };

  const toggleWishlist = async (productId, e) => {
    if (e) e.stopPropagation();
    
    if (!user) {
      toast.error('עליך להתחבר כדי להוסיף למשאלות');
      return;
    }

    try {
      const isCurrentlyWishlisted = wishlist.has(productId);
      
      if (isCurrentlyWishlisted) {
        await axios.delete(`/api/wishlist/${productId}`);
        setWishlist(prev => {
          const newWishlist = new Set(prev);
          newWishlist.delete(productId);
          return newWishlist;
        });
        toast.success('המוצר הוסר מרשימת המשאלות');
      } else {
        await axios.post('/api/wishlist', { product_id: productId });
        setWishlist(prev => {
          const newWishlist = new Set(prev);
          newWishlist.add(productId);
          return newWishlist;
        });
        toast.success('המוצר נוסף לרשימת המשאלות');
      }
    } catch (error) {
      toast.error('שגיאה בעדכון רשימת המשאלות');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(price);
  };

  const getCurrentPrice = (product) => {
    if (user?.role === 'company_admin' || user?.role === 'company_user') {
      return product.price_b2b || product.price_b2c;
    }
    return product.price_b2c;
  };

  const itemsPerView = 4;
  const maxIndex = Math.max(0, recentlyViewed.length - itemsPerView);

  const nextSlide = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        {showTitle && (
          <div className="flex items-center space-x-2 mb-6">
            <EyeIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">צפיתי לאחרונה</h2>
          </div>
        )}
        <div className="flex space-x-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-1">
              <div className="aspect-square bg-gray-200 rounded-lg animate-pulse mb-3"></div>
              <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recentlyViewed.length === 0) {
    return null;
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      {showTitle && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <EyeIcon className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">צפיתי לאחרונה</h2>
            <span className="text-sm text-gray-500">({recentlyViewed.length})</span>
          </div>
          <button
            onClick={clearRecentlyViewed}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            נקה הכל
          </button>
        </div>
      )}

      <div className="relative">
        {/* Navigation Buttons */}
        {recentlyViewed.length > itemsPerView && (
          <>
            <button
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <button
              onClick={nextSlide}
              disabled={currentIndex >= maxIndex}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Products Grid */}
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(${currentIndex * (100 / itemsPerView)}%)` }}
          >
            {recentlyViewed.map((product) => (
              <div
                key={product.id}
                className="flex-shrink-0 w-1/4 px-2"
                style={{ minWidth: `${100 / itemsPerView}%` }}
              >
                <div className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeFromRecentlyViewed(product.id);
                    }}
                    className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-600" />
                  </button>

                  {/* Quick Actions */}
                  <div className="absolute top-2 left-2 z-10 flex flex-col space-y-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Wishlist Button */}
                    <button
                      onClick={(e) => toggleWishlist(product.id, e)}
                      className={`p-1.5 rounded-full shadow-sm transition-colors ${
                        wishlist.has(product.id)
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
                      }`}
                    >
                      {wishlist.has(product.id) ? (
                        <HeartIconSolid className="h-4 w-4" />
                      ) : (
                        <HeartIcon className="h-4 w-4" />
                      )}
                    </button>

                    {/* Add to Cart Button */}
                    <button
                      onClick={(e) => handleAddToCart(product, e)}
                      disabled={product.stock_quantity === 0}
                      className="p-1.5 rounded-full bg-white/90 text-gray-600 hover:bg-white hover:text-primary-600 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ShoppingCartIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <Link to={`/products/${product.id}`} className="block">
                    {/* Product Image */}
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={product.images[0].startsWith('http') ? product.images[0] : `/api${product.images[0]}`}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-12 h-12 bg-gray-300 rounded-lg"></div>
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-3">
                      <h3 className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {product.name}
                      </h3>
                      
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-primary-600 font-bold text-sm">
                          {formatPrice(getCurrentPrice(product))}
                        </span>
                        
                        {product.sku && (
                          <span className="text-xs text-gray-500">
                            {product.sku}
                          </span>
                        )}
                      </div>

                      {/* Stock Status */}
                      <div className="mt-1">
                        {product.stock_quantity > 0 ? (
                          <span className="text-xs text-green-600 font-medium">במלאי</span>
                        ) : (
                          <span className="text-xs text-red-600 font-medium">אזל מהמלאי</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator */}
        {recentlyViewed.length > itemsPerView && (
          <div className="flex justify-center mt-4 space-x-2">
            {Array.from({ length: Math.ceil(recentlyViewed.length / itemsPerView) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  Math.floor(currentIndex / itemsPerView) === index
                    ? 'bg-primary-600'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* View All Link */}
      {recentlyViewed.length >= maxItems && (
        <div className="mt-4 text-center">
          <Link
            to="/products?recently_viewed=true"
            className="text-primary-600 hover:text-primary-700 font-medium text-sm"
          >
            צפה בכל המוצרים שצפיתי בהם
          </Link>
        </div>
      )}
    </div>
  );
};

export default RecentlyViewed;
